import React, { useState, useEffect } from 'react';
import './Settings.css';
import { API_BASE } from '../config';
import { useAuth } from '../context/AuthContext';
import SystemDiagnosticsPanel from '../components/SystemDiagnosticsPanel';

const ROLES = [
  { key: 'ADMIN', label: 'Administrator', desc: 'Full access to settings, billing, member management, and all APIs.' },
  { key: 'MANAGER', label: 'Manager', desc: 'Manage API keys, schemas, policies, and view audit logs.' },
  { key: 'SECURITY_OFFICER', label: 'Security Officer', desc: 'Configure & enforce data masking policies, schema registries, and compliance.' },
  { key: 'AUDITOR', label: 'Compliance Auditor', desc: 'Read-only access to audit logs, compliance dashboards, and policies.' },
  { key: 'USER', label: 'Developer / User', desc: 'Standard access to tokenization, detokenization, and gateway APIs.' },
];

const Settings = () => {
  const { token, tenant } = useAuth();

  const [activeTab, setActiveTab] = useState('team'); // 'team' | 'webhooks' | 'diagnostics'

  // Team management state
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [teamLoading, setTeamLoading] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('USER');
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [generatedInvite, setGeneratedInvite] = useState(null);
  const [toastMessage, setToastMessage] = useState('');

  // Webhook state
  const [status, setStatus] = useState('loading'); // loading | ready | error
  const [errorMessage, setErrorMessage] = useState('');
  const [tokenizedUrl, setTokenizedUrl] = useState('');
  const [detokenizedUrl, setDetokenizedUrl] = useState('');
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');

  // Webhook diagnostics modal
  const [testingType, setTestingType] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [showTestModal, setShowTestModal] = useState(false);

  const showToast = (msg) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(''), 3500);
  };

  /* =====================
   * TEAM MANAGEMENT API
   * ===================== */

  const fetchTeamMembers = async () => {
    if (!token) return;
    setTeamLoading(true);
    try {
      const res = await fetch(`${API_BASE}/admin/team/members`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setMembers(data);
      }
    } catch (e) {
      console.error('Failed to load team members:', e);
    } finally {
      setTeamLoading(false);
    }
  };

  const fetchInvitations = async () => {
    if (!token) return;
    try {
      const res = await fetch(`${API_BASE}/admin/team/invitations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (res.ok) {
        const data = await res.json();
        setInvitations(data);
      }
    } catch (e) {
      console.error('Failed to load invitations:', e);
    }
  };

  const handleSendInvite = async (e) => {
    e.preventDefault();
    setInviteLoading(true);
    setInviteError('');
    setGeneratedInvite(null);

    try {
      const baseUrl = window.location.origin;
      const res = await fetch(`${API_BASE}/admin/team/invite`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({
          email: inviteEmail.trim(),
          role: inviteRole,
          baseUrl
        })
      });

      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to issue invitation.');
      }

      setGeneratedInvite(data);
      setInviteEmail('');
      fetchInvitations();
      showToast('✓ Team invitation issued successfully!');
    } catch (err) {
      setInviteError(err.message);
    } finally {
      setInviteLoading(false);
    }
  };

  const handleUpdateRole = async (memberId, newRole) => {
    try {
      const res = await fetch(`${API_BASE}/admin/team/members/${memberId}/role`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ role: newRole })
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to update role');
      }

      showToast(`✓ Member role updated to ${newRole}`);
      fetchTeamMembers();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleRemoveMember = async (memberId, email) => {
    if (!window.confirm(`Are you sure you want to remove ${email} from this workspace?`)) return;
    try {
      const res = await fetch(`${API_BASE}/admin/team/members/${memberId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to remove member');
      }

      showToast('✓ Member removed from workspace');
      fetchTeamMembers();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleRevokeInvite = async (invitationId) => {
    if (!window.confirm('Revoke this invitation token?')) return;
    try {
      const res = await fetch(`${API_BASE}/admin/team/invitations/${invitationId}/revoke`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || 'Failed to revoke invitation');
      }

      showToast('✓ Invitation token revoked');
      fetchInvitations();
    } catch (err) {
      alert(err.message);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    showToast('📋 Copied invitation link to clipboard!');
  };

  /* =====================
   * WEBHOOK MANAGEMENT
   * ===================== */

  const handleTestWebhook = async (type) => {
    setTestingType(type);
    setTestResult(null);
    setShowTestModal(true);
    try {
      const res = await fetch(`${API_BASE}/dashboard/webhooks/test`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ type })
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.message || `HTTP ${res.status}`);
      }
      const data = await res.json();
      setTestResult(data);
    } catch (err) {
      setTestResult({
        targetType: type.toUpperCase(),
        targetUrl: type === 'tokenized' ? tokenizedUrl : detokenizedUrl,
        success: false,
        error: err.message,
        httpStatusCode: 0,
        latencyMs: 0,
        signature: 'N/A',
        responseBody: ''
      });
    } finally {
      setTestingType(null);
    }
  };

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage('Authentication required');
      return;
    }

    fetchTeamMembers();
    fetchInvitations();

    fetch(`${API_BASE}/dashboard/webhooks`, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
      .then(res => {
        if (!res.ok) {
          throw new Error(`Access denied (${res.status})`);
        }
        return res.json();
      })
      .then(data => {
        setTokenizedUrl(data.tokenizedUrl || '');
        setDetokenizedUrl(data.detokenizedUrl || '');
        setStatus('ready');
      })
      .catch(err => {
        console.error('Webhook access error:', err);
        setStatus('ready'); // Allow team tab to still function smoothly
      });
  }, [token]);

  const saveWebhooks = async () => {
    setMessage('');
    try {
      const res = await fetch(`${API_BASE}/dashboard/webhooks`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ tokenizedUrl, detokenizedUrl }),
      });

      if (!res.ok) {
        throw new Error('Save failed');
      }

      setMessage('Webhook settings saved successfully');
      setMessageType('success');
    } catch (err) {
      console.error(err);
      setMessage('Failed to save webhook settings');
      setMessageType('error');
    }
  };

  const resetSecret = async () => {
    try {
      const res = await fetch(`${API_BASE}/dashboard/webhooks/reset`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!res.ok) {
        throw new Error('Reset failed');
      }

      setMessage('Webhook secret reset successfully');
      setMessageType('success');
    } catch (err) {
      console.error(err);
      setMessage('Failed to reset webhook secret');
      setMessageType('error');
    }
  };

  return (
    <div className="container settings-page">
      <header className="page-header">
        <h2>Enterprise Workspace Settings</h2>
        <p className="page-subtitle">
          Manage team member access, role-based governance, and webhook delivery endpoints
        </p>
      </header>

      {toastMessage && (
        <div className="settings-toast">
          <span>{toastMessage}</span>
          <button onClick={() => setToastMessage('')}>✕</button>
        </div>
      )}

      {/* Settings Navigation Tabs */}
      <div className="settings-tabs-nav">
        <button
          className={`settings-tab-btn ${activeTab === 'team' ? 'active' : ''}`}
          onClick={() => setActiveTab('team')}
        >
          👥 Team & Role-Based Access Control (RBAC)
        </button>
        <button
          className={`settings-tab-btn ${activeTab === 'webhooks' ? 'active' : ''}`}
          onClick={() => setActiveTab('webhooks')}
        >
          🔗 Webhook Subscriptions & Security
        </button>
        <button
          className={`settings-tab-btn ${activeTab === 'diagnostics' ? 'active' : ''}`}
          onClick={() => setActiveTab('diagnostics')}
        >
          📊 System Diagnostics & Health
        </button>
      </div>

      {/* ====================================================
          TAB 1: TEAM & ACCESS CONTROL (RBAC)
         ==================================================== */}
      {activeTab === 'team' && (
        <div className="team-settings-container">
          <div className="team-header-row">
            <div>
              <h3 className="section-title">Organization Members</h3>
              <p className="section-subtitle">
                Configure member privileges and grant granular permissions across your workspace.
              </p>
            </div>
            <button
              className="btn btn-primary btn-invite-member"
              onClick={() => {
                setInviteModalOpen(true);
                setGeneratedInvite(null);
                setInviteError('');
              }}
            >
              + Invite Team Member
            </button>
          </div>

          {/* Role Guide Pills */}
          <div className="role-guide-card">
            <span className="role-guide-title">Role Privileges Summary:</span>
            <div className="role-pills-row">
              <span className="role-pill role-admin">Admin (Full Control)</span>
              <span className="role-pill role-manager">Manager (Keys, Schemas, Policies)</span>
              <span className="role-pill role-security_officer">Security Officer (Masking & Policies)</span>
              <span className="role-pill role-auditor">Auditor (Read-Only Logs)</span>
              <span className="role-pill role-user">Developer (Gateway & API)</span>
            </div>
          </div>

          {/* Members Table */}
          <div className="members-table-wrapper">
            <table className="members-table">
              <thead>
                <tr>
                  <th>Member</th>
                  <th>Email</th>
                  <th>Assigned Role</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {members.map((m) => (
                  <tr key={m.id || m.email}>
                    <td>
                      <div className="member-cell">
                        <div className="member-avatar">
                          {(m.name || m.email || 'U').charAt(0).toUpperCase()}
                        </div>
                        <div>
                          <strong>{m.name || 'Member'}</strong>
                          {m.isOwner && <span className="owner-badge">👑 Workspace Owner</span>}
                        </div>
                      </div>
                    </td>
                    <td>
                      <code>{m.email}</code>
                    </td>
                    <td>
                      {m.isOwner ? (
                        <span className="role-pill role-admin">ADMINISTRATOR</span>
                      ) : (
                        <select
                          className="role-select"
                          value={m.role}
                          onChange={(e) => handleUpdateRole(m.id, e.target.value)}
                        >
                          {ROLES.map((r) => (
                            <option key={r.key} value={r.key}>
                              {r.label}
                            </option>
                          ))}
                        </select>
                      )}
                    </td>
                    <td>
                      <span className="status-badge active">
                        {m.status || 'ACTIVE'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      {!m.isOwner ? (
                        <button
                          className="btn-member-action delete"
                          onClick={() => handleRemoveMember(m.id, m.email)}
                          title="Remove from workspace"
                        >
                          Remove
                        </button>
                      ) : (
                        <span className="text-muted" style={{ fontSize: '0.8rem' }}>Primary</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pending Invitations Section */}
          <div className="invitations-section">
            <h4 className="sub-title">Pending Invitations ({invitations.length})</h4>
            {invitations.length === 0 ? (
              <p className="text-muted" style={{ fontSize: '0.88rem' }}>No pending team invitations.</p>
            ) : (
              <div className="invitations-table-wrapper">
                <table className="invitations-table">
                  <thead>
                    <tr>
                      <th>Invitee Email</th>
                      <th>Target Role</th>
                      <th>Status</th>
                      <th>Expires In</th>
                      <th style={{ textAlign: 'right' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invitations.map((inv) => {
                      const inviteUrl = `${window.location.origin}/invite/accept?token=${inv.token}`;
                      return (
                        <tr key={inv.id}>
                          <td><strong>{inv.email}</strong></td>
                          <td>
                            <span className={`role-pill role-${(inv.role || '').toLowerCase()}`}>
                              {inv.role}
                            </span>
                          </td>
                          <td>
                            <span className="status-badge suspended">PENDING</span>
                          </td>
                          <td>
                            <span style={{ fontSize: '0.82rem', color: 'var(--text-secondary)' }}>
                              {inv.expiresAt ? new Date(inv.expiresAt).toLocaleDateString() : '7 days'}
                            </span>
                          </td>
                          <td style={{ textAlign: 'right' }}>
                            <button
                              className="btn-copy-link"
                              onClick={() => copyToClipboard(inviteUrl)}
                              title="Copy invitation link"
                            >
                              📋 Copy Link
                            </button>
                            <button
                              className="btn-member-action delete"
                              onClick={() => handleRevokeInvite(inv.id)}
                              style={{ marginLeft: '6px' }}
                            >
                              Revoke
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ====================================================
          TAB 2: WEBHOOK SUBSCRIPTIONS & SECURITY
         ==================================================== */}
      {activeTab === 'webhooks' && (
        <section className="page-card">
          <h3>Webhook Subscriptions & Delivery Endpoints</h3>

          <div className="form-field webhook-input-group">
            <span>Tokenized Webhook URL</span>
            <div className="input-with-action">
              <input
                className="input"
                type="url"
                value={tokenizedUrl}
                onChange={e => setTokenizedUrl(e.target.value)}
                placeholder="https://example.com/tokenized"
              />
              <button
                type="button"
                className="btn btn-secondary btn-test-ping"
                onClick={() => handleTestWebhook('tokenized')}
                disabled={!tokenizedUrl || Boolean(testingType)}
              >
                {testingType === 'tokenized' ? '⏳ Pinging...' : '⚡ Test Ping'}
              </button>
            </div>
            <small className="field-hint">Invoked in real-time whenever sensitive data is ingested and tokenized</small>
          </div>

          <div className="form-field webhook-input-group">
            <span>Detokenized Webhook URL</span>
            <div className="input-with-action">
              <input
                className="input"
                type="url"
                value={detokenizedUrl}
                onChange={e => setDetokenizedUrl(e.target.value)}
                placeholder="https://example.com/detokenized"
              />
              <button
                type="button"
                className="btn btn-secondary btn-test-ping"
                onClick={() => handleTestWebhook('detokenized')}
                disabled={!detokenizedUrl || Boolean(testingType)}
              >
                {testingType === 'detokenized' ? '⏳ Pinging...' : '⚡ Test Ping'}
              </button>
            </div>
            <small className="field-hint">Invoked whenever an authorized actor detokenizes a vault token</small>
          </div>

          <div className="form-actions">
            <button className="btn btn-primary" onClick={saveWebhooks}>
              Save Webhook Settings
            </button>

            <button className="btn btn-secondary" onClick={resetSecret}>
              Reset Webhook Secret
            </button>
          </div>

          {message && (
            <div className={`alert ${messageType}`}>
              {message}
            </div>
          )}
        </section>
      )}

      {/* ====================================================
          TAB 3: SYSTEM DIAGNOSTICS & HEALTH
         ==================================================== */}
      {activeTab === 'diagnostics' && (
        <SystemDiagnosticsPanel />
      )}

      {/* ====================================================
          INVITE TEAM MEMBER MODAL
         ==================================================== */}
      {inviteModalOpen && (
        <div className="modal-overlay" onClick={() => setInviteModalOpen(false)}>
          <div className="modal-content invite-modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3>👥 Invite Team Member</h3>
                <p className="modal-subtitle">
                  Send a secure cryptographic invitation link with role-based access
                </p>
              </div>
              <button className="policy-modal-close" onClick={() => setInviteModalOpen(false)}>✕</button>
            </div>

            {inviteError && (
              <div className="alert error" style={{ marginBottom: '1rem' }}>
                {inviteError}
              </div>
            )}

            {generatedInvite ? (
              <div className="invite-success-card">
                <div className="success-icon">🎉</div>
                <h4>Invitation Generated!</h4>
                <p>Share this link with <strong>{generatedInvite.email}</strong> to join as <strong>{generatedInvite.role}</strong>:</p>
                <div className="invite-link-copy-box">
                  <input
                    type="text"
                    readOnly
                    value={generatedInvite.inviteUrl}
                    className="invite-url-input"
                  />
                  <button
                    className="btn btn-primary"
                    onClick={() => copyToClipboard(generatedInvite.inviteUrl)}
                  >
                    📋 Copy Link
                  </button>
                </div>
                <button
                  className="btn btn-secondary"
                  style={{ marginTop: '1rem', width: '100%' }}
                  onClick={() => {
                    setGeneratedInvite(null);
                    setInviteModalOpen(false);
                  }}
                >
                  Done
                </button>
              </div>
            ) : (
              <form onSubmit={handleSendInvite}>
                <div className="form-field">
                  <label>Team Member Email</label>
                  <input
                    type="email"
                    required
                    placeholder="colleague@company.com"
                    value={inviteEmail}
                    onChange={e => setInviteEmail(e.target.value)}
                    className="input"
                  />
                </div>

                <div className="form-field">
                  <label>Assign Role</label>
                  <select
                    className="input"
                    value={inviteRole}
                    onChange={e => setInviteRole(e.target.value)}
                  >
                    {ROLES.map(r => (
                      <option key={r.key} value={r.key}>
                        {r.label} - {r.desc}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="role-explainer-box">
                  <strong>Selected Role Scope:</strong>
                  <p>{ROLES.find(r => r.key === inviteRole)?.desc}</p>
                </div>

                <div className="modal-actions" style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.75rem', marginTop: '1.5rem' }}>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => setInviteModalOpen(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary"
                    disabled={inviteLoading || !inviteEmail}
                  >
                    {inviteLoading ? '⏳ Generating Invite...' : 'Generate & Send Invite'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {/* ====================================================
          LIVE WEBHOOK PING DIAGNOSTICS MODAL
         ==================================================== */}
      {showTestModal && (
        <div className="modal-overlay webhook-modal-overlay" onClick={() => setShowTestModal(false)}>
          <div className="modal-content webhook-diagnostics-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <div>
                <h3>⚡ Webhook Test Dispatcher Diagnostics</h3>
                <p className="modal-subtitle">
                  Live verification of HMAC-SHA256 signature transmission and target listener HTTP response
                </p>
              </div>
              <button className="policy-modal-close" onClick={() => setShowTestModal(false)}>✕</button>
            </div>

            {testingType && (
              <div className="webhook-test-loading">
                <div className="spinner"></div>
                <p>Dispatching test event (<code>webhook.test_ping</code>) to target URL...</p>
              </div>
            )}

            {testResult && (
              <div className="webhook-test-body">
                <div className="webhook-status-header">
                  <span className={`status-badge ${testResult.success ? 'active' : 'suspended'}`}>
                    {testResult.success ? `✅ HTTP ${testResult.httpStatusCode || 200} OK` : testResult.httpStatusCode ? `❌ HTTP ${testResult.httpStatusCode} Error` : '❌ Connection Failed'}
                  </span>
                  <span className="latency-badge">
                    ⏱️ Latency: <strong>{testResult.latencyMs} ms</strong>
                  </span>
                  <span className="target-type-badge">
                    Target: <strong>{testResult.targetType}</strong>
                  </span>
                </div>

                <div className="webhook-details-list">
                  <div className="detail-row">
                    <span className="detail-key">Target Endpoint:</span>
                    <code className="detail-val">{testResult.targetUrl || '(Not configured)'}</code>
                  </div>
                  <div className="detail-row">
                    <span className="detail-key">HMAC Signature Header:</span>
                    <code className="detail-val sig-val">{testResult.signature}</code>
                  </div>
                  {testResult.error && (
                    <div className="detail-row error-row">
                      <span className="detail-key">Failure Diagnostics:</span>
                      <span className="error-text">{testResult.error}</span>
                    </div>
                  )}
                </div>

                {testResult.responseBody && (
                  <div className="response-preview-box">
                    <div className="preview-label">Target Listener Response Body:</div>
                    <pre className="response-pre">{testResult.responseBody}</pre>
                  </div>
                )}
              </div>
            )}

            <div className="modal-actions" style={{ marginTop: '1.25rem', display: 'flex', justifyContent: 'flex-end' }}>
              <button className="btn btn-secondary" onClick={() => setShowTestModal(false)}>
                Close Viewer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
